// Pure collision-detection primitives (AD-1). Pure TypeScript leaf: no DOM,
// canvas, framework, network, storage, or wall-clock access, and no AU, day, or
// degree literal (SI metres and radians only, AD-5). Deterministic given inputs.
//
// FR-13 / AD-10: contact is decided from SI world positions and summed SI
// `collisionRadius` values ONLY. These functions know nothing about sprites,
// physical radius, orbit-line geometry, projected pixels, or scale mode; that is
// exactly what makes "sprite overlap is not a collision" and "orbit-line
// intersection is not a collision" true by construction (Story 4.1 AC 2, AC 3).

/**
 * Point-overlap test: `true` when the SI centre distance between two points is at
 * most the summed collision radii (touching counts as contact). Uses squared
 * distance to avoid a sqrt: `(ax-bx)^2 + (ay-by)^2 <= sumRadii^2`.
 */
export function pointsInContact(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  sumRadii: number
): boolean {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy <= sumRadii * sumRadii;
}

/**
 * Minimum squared distance between two points each moving LINEARLY on the
 * parameter interval `[0, 1]`, from `a0 -> a1` and `b0 -> b1` (the closest approach
 * of two moving points). This is the anti-tunnelling primitive: it inspects the
 * whole swept sub-interval, not just the endpoints, so a body cannot pass through
 * another between two evaluated positions.
 *
 * Let `w(t) = (a0 - b0) + t * dv`, where `dv = (a1 - a0) - (b1 - b0)` is the
 * relative velocity over the interval. The closest approach is at
 * `t* = -(w0 . dv) / (dv . dv)`, clamped to `[0, 1]`. When `dv . dv === 0` the two
 * points move together (zero relative velocity): separation is constant, so the
 * closest-approach parameter is undefined (0/0). Guard it and return the constant
 * endpoint separation rather than dividing by zero (which would yield NaN and
 * silently defeat the `<= sumRadii^2` test, e.g. two bodies sharing one orbit).
 */
export function sweptMinSeparationSq(
  a0: { x: number; y: number },
  a1: { x: number; y: number },
  b0: { x: number; y: number },
  b1: { x: number; y: number }
): number {
  const w0x = a0.x - b0.x;
  const w0y = a0.y - b0.y;
  const dvx = a1.x - a0.x - (b1.x - b0.x);
  const dvy = a1.y - a0.y - (b1.y - b0.y);
  const dvv = dvx * dvx + dvy * dvy;
  // Zero relative velocity: separation is constant over the sub-interval.
  if (dvv === 0) return w0x * w0x + w0y * w0y;
  let t = -(w0x * dvx + w0y * dvy) / dvv;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const cx = w0x + t * dvx;
  const cy = w0y + t * dvy;
  return cx * cx + cy * cy;
}

/**
 * Swept contact test over one sub-interval: `true` when the closest approach of the
 * two linearly-moving points is within the summed collision radii. This is the
 * per-sub-segment primitive `resolveCollisions` runs after sub-sampling an orbital
 * arc finely enough that the linear approximation of each body's motion is accurate.
 */
export function segmentsInContact(
  a0: { x: number; y: number },
  a1: { x: number; y: number },
  b0: { x: number; y: number },
  b1: { x: number; y: number },
  sumRadii: number
): boolean {
  return sweptMinSeparationSq(a0, a1, b0, b1) <= sumRadii * sumRadii;
}
